import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import {
    fetchWidgetChatMessages,
    markWidgetMessageRead,
    resetNumberOfUnreadMessages,
} from 'flow/actions/channels-actions';
import MessageInput from 'flow/components/pbx/live-chat/MessageInput';
import MessagesPage from 'flow/components/pbx/live-chat/MessagesPage';
import PbxChannelMessages from 'flow/components/pbx/PbxChannelMessageList';
import Loading from 'flow/components/ui-components/Loading';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectAppIsVisibleAndFocused } from 'flow/selectors/app-selectors';
import {
    selectLastMessageIsRead,
    selectLastMessageKey,
    selectWidgetMessages,
} from 'flow/selectors/channel-selectors';
import { ChannelKey, ChatSessionKey } from 'flow/types/EntityKeys';
import { shouldFetchCompleteMessages } from 'flow/utils/message-utils';

const PbxLiveChatMessages = () => {
    const dispatch = useAppDispatch();

    const { channelKey, chatSessionKey } = useParams<{
        channelKey: ChannelKey;
        chatSessionKey: ChatSessionKey;
    }>();

    const appIsVisibleAndFocused = useAppSelector(selectAppIsVisibleAndFocused);
    const messages = useAppSelector((state) =>
        selectWidgetMessages(state, chatSessionKey),
    );
    const lastMessageIsRead = useAppSelector((state) =>
        selectLastMessageIsRead(state, chatSessionKey),
    );
    const lastMessageKey = useAppSelector((state) =>
        selectLastMessageKey(state, chatSessionKey),
    );

    useEffect(() => {
        dispatch(resetNumberOfUnreadMessages(chatSessionKey, channelKey));
        dispatch(fetchWidgetChatMessages(chatSessionKey, null, 'contact'));
    }, [channelKey, chatSessionKey, dispatch]);

    // Track previous messages for update logic
    const prevMessagesRef = useRef(messages);

    useEffect(() => {
        const prevMessages = prevMessagesRef.current;

        if (prevMessages !== messages) {
            /**
             * Messages from Top don't always have all the information we'd like. If we get a new message from Top
             * without some info, refetch the new messages from the API.
             */
            if (
                prevMessages &&
                messages &&
                !prevMessages.isEmpty() &&
                !messages.isEmpty()
            ) {
                const prevLastMessage = prevMessages.last();
                const nextLastMessage = messages.last();

                if (
                    shouldFetchCompleteMessages(
                        prevLastMessage,
                        nextLastMessage,
                    )
                ) {
                    dispatch(
                        fetchWidgetChatMessages(
                            chatSessionKey,
                            prevLastMessage.get('key'),
                            'contact',
                        ),
                    );
                }
            }
            prevMessagesRef.current = messages;
        }
    }, [chatSessionKey, dispatch, messages]);

    useEffect(() => {
        if (appIsVisibleAndFocused && lastMessageKey && !lastMessageIsRead) {
            dispatch(
                markWidgetMessageRead(
                    channelKey,
                    chatSessionKey,
                    lastMessageKey,
                ),
            );
        }
    }, [
        appIsVisibleAndFocused,
        channelKey,
        chatSessionKey,
        dispatch,
        lastMessageIsRead,
        lastMessageKey,
    ]);

    if (!messages) {
        return <Loading />;
    }

    return (
        <MessagesPage
            channelKey={channelKey}
            chatSessionKey={chatSessionKey}
            footer={
                <MessageInput
                    chatSessionKey={chatSessionKey}
                    channelKey={channelKey}
                />
            }
        >
            <PbxChannelMessages
                key={'channel-message-list-' + chatSessionKey}
                messages={messages}
                chatSessionKey={chatSessionKey}
            />
        </MessagesPage>
    );
};

export default PbxLiveChatMessages;
